import React, { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { QrCode, MessageCircle, CheckCircle, TrendingUp, User, Bot } from 'lucide-react'
import { useTranslation } from 'react-i18next'

gsap.registerPlugin(ScrollTrigger)

const HowItWorks = () => {
  const { t } = useTranslation()
  const sectionRef = useRef(null)
  const [visibleMessages, setVisibleMessages] = useState(0)

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.chat-container',
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            end: 'bottom 30%',
            toggleActions: 'play none none reverse',
            onEnter: () => {
              const timer = setInterval(() => {
                setVisibleMessages(prev => {
                  if (prev < chatMessages.length) {
                    return prev + 1
                  } else {
                    clearInterval(timer)
                    return prev
                  }
                })
              }, 1000)
            }
          }
        }
      )
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  const chatMessages = [
    {
      type: 'user',
      message: t('howItWorks.chatMessages.user1'),
      step: t('howItWorks.steps.scan.title'),
      description: t('howItWorks.steps.scan.description'),
      icon: QrCode
    },
    {
      type: 'bot',
      message: t('howItWorks.chatMessages.bot1'),
      step: t('howItWorks.steps.converse.title'),
      description: t('howItWorks.steps.converse.description'),
      icon: MessageCircle
    },
    {
      type: 'user',
      message: t('howItWorks.chatMessages.user2'),
      step: t('howItWorks.steps.decide.title'),
      description: t('howItWorks.steps.decide.description'),
      icon: CheckCircle
    },
    {
      type: 'bot',
      message: t('howItWorks.chatMessages.bot2'),
      step: t('howItWorks.steps.convert.title'),
      description: t('howItWorks.steps.convert.description'),
      icon: TrendingUp
    },
    {
      type: 'user',
      message: t('howItWorks.chatMessages.user3'),
      step: '',
      description: '',
      icon: null
    },
    {
      type: 'bot',
      message: t('howItWorks.chatMessages.bot3'),
      step: '',
      description: '',
      icon: null
    }
  ]

  const stepColors = {
    [t('howItWorks.steps.scan.title')]: 'bg-purple-500',
    [t('howItWorks.steps.converse.title')]: 'bg-blue-500',
    [t('howItWorks.steps.decide.title')]: 'bg-green-500',
    [t('howItWorks.steps.convert.title')]: 'bg-orange-500'
  }

  return (
    <section id="how-it-works" ref={sectionRef} className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
          >
            {t('howItWorks.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {t('howItWorks.subtitle')}
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Steps Overview - Mobile: Stack vertically */}
          <div className="order-2 lg:order-1">
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-4 sm:gap-6">
              {chatMessages.slice(0, 4).map((msg, index) => {
                const Icon = msg.icon
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="flex flex-col lg:flex-row items-start lg:items-center space-y-3 lg:space-y-0 lg:space-x-4 p-4 sm:p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 ${stepColors[msg.step]} rounded-xl flex items-center justify-center`}>
                      <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2">{msg.step}</h3>
                      <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{msg.description}</p>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>

          {/* Chat Interface */}
          <div className="order-1 lg:order-2">
            <div className="chat-container bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-2xl max-w-md mx-auto lg:max-w-none">
              {/* Chat Header */}
              <div className="flex items-center justify-between mb-4 sm:mb-6 pb-3 sm:pb-4 border-b border-gray-700">
                <div className="flex items-center space-x-3 rtl:space-x-reverse">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                    <Bot className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-semibold text-white">{t('howItWorks.chatHeader.title')}</h3>
                    <p className="text-xs text-gray-400">{t('howItWorks.chatHeader.status')}</p>
                  </div>
                </div>
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="space-y-3 sm:space-y-4 h-64 sm:h-80 overflow-y-auto">
                {chatMessages.slice(0, visibleMessages).map((msg, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-xs lg:max-w-sm p-3 sm:p-4 rounded-2xl ${
                      msg.type === 'user' 
                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-br-md rtl:rounded-br-2xl rtl:rounded-bl-md' 
                        : 'bg-gray-700 text-white rounded-bl-md rtl:rounded-bl-2xl rtl:rounded-br-md'
                    }`}>
                      <div className="flex items-start space-x-2 rtl:space-x-reverse">
                        {msg.type === 'bot' && (
                          <Bot className="w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0" />
                        )}
                        {msg.type === 'user' && (
                          <User className="w-4 h-4 text-purple-200 mt-0.5 flex-shrink-0" />
                        )}
                        <p className="text-xs sm:text-sm leading-relaxed">{msg.message}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Typing Indicator */}
              {visibleMessages < chatMessages.length && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start mt-3 sm:mt-4"
                >
                  <div className="bg-gray-700 p-3 rounded-2xl rounded-bl-md">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default HowItWorks